import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styles from './style.module.css';
import Typography from '../../Typography/Typography';
import FlatButtons from '../../FlatButton/FlatButton';
import { OpenXLogo } from '../../../assets';
import { SellerDashboardAnalytics, SellerDashboardIcon, SellerDashboardProduct, SellerDashboardProfile, SellerDashboardSettings, SellerDashboardStore, SideNavCollapseIcon, StoreCartGray, SellerDashboardDropIcon } from '../../../assets/svgs';
import { setSelectedStore } from '../../../redux/slices/selectedStoreSlice';
import { useGetStoreByOwnerQuery } from '../../../redux/services/storesApi';
import type { RootState } from '../../../redux/store';

interface SidebarItem {
    id: string;
    label: string;
    icon: string;
    path: string;
    category?: 'main' | 'store' | 'settings';
}

interface VerticalSidebarProps {
    activeItem?: string;
    onItemClick?: (itemId: string, path: string) => void;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    useRouting?: boolean;
    className?: string;
    isMobile?: boolean;
}

export const VerticalSidebar: React.FC<VerticalSidebarProps> = ({
    activeItem: propActiveItem,
    onItemClick,
    isCollapsed: propIsCollapsed,
    onToggleCollapse,
    useRouting = true,
    className = '',
    isMobile = false
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const selectedStoreId = useSelector((state: RootState) => state.selectedStore.storeId);
    
    const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
    const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
    
    const isCollapsed = isMobile ? false : (propIsCollapsed !== undefined ? propIsCollapsed : internalIsCollapsed);

    // Fetch stores from API
    const { data: storesData, isLoading: isLoadingStores, isError: isStoresError } = useGetStoreByOwnerQuery();

    const getActiveItemFromPath = (pathname: string): string => {
        const pathMap: Record<string, string> = {
            '/seller-dashboard': 'dashboard',
            '/orders': 'orders',
            '/analytics': 'analytics',
            '/products': 'products',
            '/store': 'store',
            '/profile': 'profile',
            '/profile/edit': 'profile',
            '/preferences': 'preferences'
        };
        return pathMap[pathname] || 'dashboard';
    };

    const activeItem = useRouting 
        ? getActiveItemFromPath(location.pathname)
        : (propActiveItem || 'dashboard');

    const sidebarItems: SidebarItem[] = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            icon: 'dashboard',
            path: '/seller-dashboard',
            category: 'main'
        },
        {
            id: 'orders',
            label: 'Order Management',
            icon: 'orders',
            path: '/orders',
            category: 'main'
        },
        {
            id: 'analytics',
            label: 'Analytics',
            icon: 'analytics',
            path: '/analytics',
            category: 'main'
        },
        {
            id: 'products',
            label: 'Product List',
            icon: 'products',
            path: '/products',
            category: 'store'
        },
        {
            id: 'store',
            label: 'Store',
            icon: 'store',
            path: '/store',
            category: 'store'
        },
        {
            id: 'profile',
            label: 'Profile',
            icon: 'profile',
            path: '/profile',
            category: 'settings'
        },
        {
            id: 'preferences',
            label: 'Preference Settings',
            icon: 'settings',
            path: '/preferences',
            category: 'settings'
        }
    ];

    const handleToggleCollapse = () => {
        // On mobile, close the sidebar when collapse button is clicked
        if (isMobile && onToggleCollapse) {
            onToggleCollapse();
        } else if (onToggleCollapse) {
            onToggleCollapse();
        } else {
            setInternalIsCollapsed(!internalIsCollapsed);
        }
    };

    const handleItemClick = (item: SidebarItem) => {
        if (onItemClick) {
            onItemClick(item.id, item.path);
        }

        if (useRouting) {
            navigate(item.path);
        }
    };

    const handleLogoClick = () => {
        navigate("/");
    };

    const handleStoreSelect = (storeId: string) => {
        dispatch(setSelectedStore(storeId));
        setIsStoreDropdownOpen(false);
    };

    const getSelectedStoreName = () => {
        if (selectedStoreId === 'all') return 'All stores';
        const store = storesData?.find(s => s._id === selectedStoreId);
        return store?.name || 'All stores';
    };

    const getIconComponent = (iconName: string, isActive: boolean) => {
        const iconClass = `${styles.verticalNavIcons} ${isCollapsed ? styles.collapsedIcon : ''} ${isActive ? styles.activeIcon : ''}`;

        switch (iconName) {
            case 'dashboard':
                return (
                    <img
                        src={SellerDashboardIcon}
                        alt='dashboard icon'
                        className={iconClass}
                    />
                );
            case 'orders':
                return (
                    <img
                        src={StoreCartGray}
                        alt='cart icon'
                        className={iconClass}
                    />
                );
            case 'analytics':
                return (
                    <img
                        src={SellerDashboardAnalytics}
                        alt='analytics icon'
                        className={iconClass}
                    />
                );
            case 'products':
                return (
                    <img 
                        src={SellerDashboardProduct}
                        alt='product icon'
                        className={iconClass}
                    />
                );
            case 'store':
                return (
                    <img
                        src={SellerDashboardStore}
                        alt='store icon'
                        className={iconClass}
                    />
                );
            case 'profile':
                return (
                    <img
                        src={SellerDashboardProfile}
                        alt='profile icon'
                        className={iconClass}
                    />
                );
            case 'settings':
                return (
                    <img
                        src={SellerDashboardSettings}
                        alt='settings icon'
                        className={iconClass}
                    />
                );
            default:
                return null;
        }
    };

    const renderCategoryLabel = (category: string) => {
        const labels = {
            main: 'MAIN MENU',
            store: 'STORE',
            settings: 'SETTINGS'
        };

        return (
            <div className={`${styles.categoryLabel} ${isCollapsed ? styles.collapsedCategory : ''}`}>
                <Typography
                    variant='bodySmall'
                    fontFamily='satoshi'
                    weight='medium'
                    className={styles.categoryText}>
                    {labels[category as keyof typeof labels]}
                </Typography>
            </div>
        );
    };

    const groupedItems = sidebarItems.reduce((acc, item) => {
        const category = item.category || 'main';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(item);
        return acc;
    }, {} as Record<string, SidebarItem[]>);

    return (
        <div 
            className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${className}`}
            style={isMobile ? { left: className === 'mobileOpen' ? '0' : '-100%' } : undefined}
        >
            <div className={styles.sidebarContent}>
                <div className={styles.logoSection}>
                    <div className={styles.logo}>
                        <OpenXLogo
                            className={styles.logoImage}
                            onClick={handleLogoClick}
                        />
                    </div>

                    <FlatButtons
                        className={styles.collapseButton}
                        onClick={handleToggleCollapse}
                    >
                        <img
                            src={SideNavCollapseIcon}
                            alt='sidebar collapse icon'
                            className={`${styles.collapseIcon} ${isCollapsed ? styles.rotated : ''}`}
                        />
                    </FlatButtons>
                </div>

                {/* Store selector - only show on mobile */}
                {isMobile && (
                    <div className={styles.sidebarStoreSelector}>
                        <div className={styles.storeSelectorHeader}>
                            <OpenXLogo className={styles.storeLogoIcon} />
                            <Typography
                                variant='bodySmall'
                                fontFamily='satoshi'
                                weight='medium'
                                className={styles.storeSelectorLabel}
                            >
                                All stores
                            </Typography>
                        </div>
                        <div className={styles.storeDropdownWrapper}>
                            <div 
                                className={`${styles.storeDropdownDisplay} ${isStoreDropdownOpen ? styles.dropdownOpen : ''}`}
                                onClick={() => setIsStoreDropdownOpen(!isStoreDropdownOpen)}
                            >
                                <div className={styles.selectedStoreDisplay}>
                                    <Typography
                                        variant='bodyRegular'
                                        fontFamily='satoshi'
                                        weight='bold'
                                        className={styles.storeDropdownText}
                                    >
                                        {isLoadingStores ? 'Loading...' : getSelectedStoreName()}
                                    </Typography>
                                </div>
                                <img
                                    src={SellerDashboardDropIcon}
                                    alt='dropdown icon'
                                    className={`${styles.storeDropdownArrow} ${isStoreDropdownOpen ? styles.arrowRotated : ''}`}
                                />
                            </div>

                            {/* Custom Dropdown Menu */}
                            {isStoreDropdownOpen && (
                                <div className={styles.customDropdownMenu}>
                                    <div 
                                        className={`${styles.dropdownItem} ${selectedStoreId === 'all' ? styles.selected : ''}`}
                                        onClick={() => handleStoreSelect('all')}
                                    >
                                        <span className={styles.storeName}>All stores</span>
                                        {selectedStoreId === 'all' && (
                                            <span className={styles.checkmark}>✓</span>
                                        )}
                                    </div>
                                    
                                    {isLoadingStores && (
                                        <div className={`${styles.dropdownItem} ${styles.loading}`}>
                                            <span className={styles.loadingSpinner}></span>
                                            <span>Loading stores...</span>
                                        </div>
                                    )}
                                    
                                    {isStoresError && (
                                        <div className={`${styles.dropdownItem} ${styles.error}`}>
                                            <span>Error loading stores</span>
                                        </div>
                                    )}
                                    
                                    {storesData && storesData.length > 0 ? (
                                        storesData.map((store) => (
                                            <div 
                                                key={store._id}
                                                className={`${styles.dropdownItem} ${selectedStoreId === store._id ? styles.selected : ''}`}
                                                onClick={() => handleStoreSelect(store._id)}
                                            >
                                                <div className={styles.storeInfo}>
                                                    {store.logo_url && (
                                                        <img src={store.logo_url} alt="" className={styles.storeLogo} />
                                                    )}
                                                    <span className={styles.storeName}>{store.name}</span>
                                                </div>
                                                {selectedStoreId === store._id && (
                                                    <span className={styles.checkmark}>✓</span>
                                                )}
                                            </div>
                                        ))
                                    ) : !isLoadingStores && !isStoresError && (
                                        <div className={`${styles.dropdownItem} ${styles.empty}`}>
                                            <span>No stores found</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <nav className={styles.navigation}>
                    {Object.entries(groupedItems).map(([category, items]) => (
                        <div key={category} className={styles.categorySection}>
                            {renderCategoryLabel(category)}
                            <ul className={styles.menuList}>
                                {items.map((item) => (
                                    <li key={item.id} className={styles.menuItem}>
                                        <button
                                            className={`${styles.menuLink} ${activeItem === item.id ? styles.active : ''
                                                }`}
                                            onClick={() => handleItemClick(item)}
                                            title={isCollapsed ? item.label : undefined}
                                        >
                                            <span className={styles.menuIcon}>
                                                {getIconComponent(item.icon, activeItem === item.id)}
                                            </span>
                                            {!isCollapsed && (
                                                <Typography
                                                    variant='heading5'
                                                    fontFamily='satoshi'
                                                    weight='regular'
                                                    className={`${styles.menuText} ${activeItem === item.id ? styles.activeText : ''}`}>
                                                    {item.label}
                                                </Typography>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>
            </div>
        </div>
    );
};